import React, { useCallback, useEffect, useState } from "react";
import { BookOpen, CheckCircle2, Eye, RefreshCw, ShieldCheck, XCircle } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const AcademyCreatorReview = () => {
  const { user } = useAuth();
  const isDirector = (user?.roles || []).includes("director");
  const [items,setItems] = useState([]);
  const [error,setError] = useState("");
  const [busy,setBusy] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/api/admin/academy/creator-courses");
      setItems(Array.isArray(data) ? data : []);
      setError("");
    } catch (requestError) {
      setError(formatApiError(requestError));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const decide = async (item, action) => {
    const notes = window.prompt(action === "approve" ? "Observación de aprobación (opcional)" : "Indica qué debe corregir el creador");
    if (notes === null || (action === "reject" && !notes.trim())) return;
    setBusy(item.slug);
    try {
      await api.post(`/api/admin/academy/creator-courses/${item.slug}/${action}`, { notes });
      await load();
    } catch (requestError) {
      setError(formatApiError(requestError));
    } finally {
      setBusy("");
    }
  };

  return (
    <div className="mx-auto max-w-[1450px] pb-12" data-testid="academy-creator-review">
      <section className="relative mb-6 overflow-hidden rounded-[30px] border border-[#d4ab5e]/20 bg-[#0d0b08] p-6 md:p-8">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_10%_15%,rgba(212,171,94,.14),transparent_34%)]" />
        <div className="relative flex flex-col justify-between gap-5 md:flex-row md:items-end">
          <div>
            <div className="flex items-center gap-2 font-mont text-[8px] uppercase tracking-[.3em] text-[#aa956e]"><ShieldCheck size={12}/> Gobierno Académico</div>
            <h1 className="mt-2 font-cinzel text-3xl text-[#f1dcae] md:text-4xl">Revisión de creadores</h1>
            <p className="mt-3 max-w-3xl font-cormorant text-xl text-[#b3a895]">
              Administración puede revisar el material. La aprobación, devolución y publicación final son decisiones exclusivas de Dirección KD9.
            </p>
          </div>
          <button onClick={load} className="inline-flex items-center gap-2 self-start rounded-xl border border-white/10 px-4 py-2.5 font-mont text-[9px] uppercase tracking-[.14em] text-[#bbaa8b]"><RefreshCw size={12}/> Actualizar</button>
        </div>
      </section>

      {!isDirector && <div className="mb-5 rounded-2xl border border-amber-400/15 bg-amber-950/10 p-4 font-mont text-[10px] leading-relaxed text-[#cdb785]">Vista administrativa de revisión. Los controles de decisión final permanecen bloqueados para este rol.</div>}
      {error && <div className="mb-5 rounded-2xl border border-red-400/20 bg-red-950/20 p-4 font-mont text-sm text-red-200">{error}</div>}

      <div className="space-y-4">
        {items.map((item) => (
          <article key={item.slug} className="rounded-[24px] border border-white/9 bg-[#100d09] p-5 md:p-6">
            <div className="flex flex-col gap-5 lg:flex-row lg:items-center">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl border border-[#d4ab5e]/20 bg-[#d4ab5e]/6 text-[#c5a463]"><BookOpen size={22}/></div>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-3">
                  <h2 className="font-cinzel text-xl text-[#ead5aa]">{item.title}</h2>
                  <span className="rounded-full border border-white/10 px-3 py-1 font-mont text-[7px] uppercase tracking-[.14em] text-[#9c907d]">{item.status}</span>
                </div>
                <div className="mt-2 flex flex-wrap gap-3 font-mont text-[8px] text-[#887e6d]"><span>Por {item.creator_name}</span><span>{item.modules_count} módulos</span><span>{item.lessons_count} lecciones</span><span>⚡ {item.energy_cost || 0} KD</span></div>
                {item.review_notes && <p className="mt-3 font-cormorant text-base text-[#b4a893]">{item.review_notes}</p>}
              </div>

              <div className="flex flex-wrap gap-2">
                <button type="button" className="inline-flex items-center gap-1 rounded-lg border border-white/10 px-3 py-2 font-mont text-[8px] uppercase tracking-[.12em] text-[#a89b85]"><Eye size={11}/> Revisar ficha</button>
                {item.status === "pending_review" && isDirector && <>
                  <button disabled={busy === item.slug} onClick={() => decide(item, "approve")} className="inline-flex items-center gap-1 rounded-lg bg-[#d4ab5e] px-3 py-2 font-mont text-[8px] font-bold uppercase tracking-[.12em] text-[#171008]"><CheckCircle2 size={11}/> Aprobar y publicar</button>
                  <button disabled={busy === item.slug} onClick={() => decide(item, "reject")} className="inline-flex items-center gap-1 rounded-lg border border-red-300/20 px-3 py-2 font-mont text-[8px] uppercase tracking-[.12em] text-red-200"><XCircle size={11}/> Solicitar cambios</button>
                </>}
              </div>
            </div>
          </article>
        ))}
        {!items.length && <div className="rounded-[26px] border border-white/8 bg-white/[.02] p-10 text-center font-cormorant text-xl text-[#a99d89]">No hay contenidos pendientes de revisión.</div>}
      </div>
    </div>
  );
};

export default AcademyCreatorReview;
